import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  RichTextEditor,
  type EditorInstance,
  type Controls,
} from '@amzn/stencil-react-components/rte';
import { TokenType } from '@amzn/stencil-react-components/hooks';
import { Col } from '@amzn/stencil-react-components/layout';

import { AttachmentsInput } from './AttachmentsInput';
import { useMessages } from '../i18n/useMessages';

// ─── Types ───────────────────────────────────────────────────────────

const RTE_BUNDLE = 'shared.ui.translations.richTextEditor';

type RteMessageKey =
  | 'rteBold'
  | 'rteItalic'
  | 'rteUnderline'
  | 'rteParagraph'
  | 'rteHeading1'
  | 'rteHeading2'
  | 'rteHeading3'
  | 'rteBulletList'
  | 'rteOrderedList'
  | 'rteLink'
  | 'rteImage'
  | 'rteUndo'
  | 'rteRedo'
  | 'rteHighlight'
  | 'rteTakePhoto'
  | 'rteDragAndDrop';

interface RichTextEditorFieldProps {
  readonly value: string;
  readonly onChange: (html: string) => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly ariaLabel?: string;
  readonly dataTestId?: string;
}

// ─── Constants ───────────────────────────────────────────────────────

const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/gif,image/webp';

// Editor viewport bounds. Stencil has no height tokens for the RTE's
// CSSValue props — rem keeps the bounds relative to the root font size.
const EDITOR_DEFAULT_HEIGHT = '12.5rem';
const EDITOR_MAX_HEIGHT = '25rem';

// ─── Custom Image Modal Content ──────────────────────────────────────

/** Mutable holder shared between one editor instance's modal content
 *  and its submit handler. Instance-scoped via useRef inside
 *  RichTextEditorField so multiple mounted editors cannot collide.
 *  `file` records the current selection intent so a FileReader that
 *  completes after the selection changed can discard its stale result. */
interface PendingImageHolder {
  dataUrl: string | null;
  file: File | null;
}

interface ImageUploadModalContentProps {
  readonly pendingImage: PendingImageHolder;
}

/**
 * Replaces the default image modal body (URL input + alt text + dims)
 * with the same camera/drag-and-drop picker used elsewhere in the app.
 * Selected file is converted to base64 and stored on the owning
 * editor's pending-image holder until the modal submits.
 */
function ImageUploadModalContent({
  pendingImage,
}: ImageUploadModalContentProps): React.ReactNode {
  const { t } = useMessages<RteMessageKey>(RTE_BUNDLE);
  const [files, setFiles] = useState<File[]>([]);

  // The modal content mounts once per modal session (Stencil's Modal
  // unmounts children on close). Start each session with a clean
  // holder so a file picked in an earlier, cancelled session can't be
  // inserted by an empty submit. NOT done in the render prop — that
  // runs on every re-render of the open modal and would wipe a
  // just-picked file.
  useEffect(() => {
    pendingImage.dataUrl = null;
    pendingImage.file = null;
  }, [pendingImage]);

  const handleFilesChange = useCallback(
    (updatedFiles: File[]) => {
      // Take only the last selected file (single image insert)
      const file = updatedFiles[updatedFiles.length - 1] ?? null;
      pendingImage.file = file;
      pendingImage.dataUrl = null;
      if (!file) {
        setFiles([]);
        return;
      }
      setFiles([file]);
      const reader = new FileReader();
      reader.onload = (e) => {
        // Re-check intent: the selection may have been cleared or
        // replaced while the read was in flight — discard stale results.
        if (pendingImage.file !== file) return;
        const dataUrl = e.target?.result;
        if (typeof dataUrl === 'string') {
          pendingImage.dataUrl = dataUrl;
        }
      };
      reader.readAsDataURL(file);
    },
    [pendingImage],
  );

  return (
    <Col gridGap="dimensions.spacing.300" padding="dimensions.spacing.300">
      <AttachmentsInput
        files={files}
        onFilesChange={handleFilesChange}
        takePhotoLabel={t('rteTakePhoto')}
        dragAndDropText={t('rteDragAndDrop')}
        accept={ACCEPTED_IMAGE_TYPES}
      />
    </Col>
  );
}

// ─── Component ───────────────────────────────────────────────────────

/**
 * Shared rich text editor field backed by Stencil's TipTap-based
 * `RichTextEditor`. Exposes a simple value/onChange string interface.
 *
 * Image support:
 * - Toolbar image button opens a simplified modal with a file picker.
 * - Paste/drag inserts images inline as base64 data URLs.
 *
 * At submit time, callers run the HTML through `uploadDescriptionImages`
 * (shared/data/media) to upload base64 images and swap in media IDs.
 */
export function RichTextEditorField({
  value,
  onChange,
  placeholder,
  disabled = false,
  ariaLabel,
  dataTestId,
}: RichTextEditorFieldProps): JSX.Element {
  const { t } = useMessages<RteMessageKey>(RTE_BUNDLE);
  const editorRef = useRef<EditorInstance | null>(null);

  // Toolbar configuration with localized tooltips. `t` is
  // referentially stable (useMessages guarantee), so this memo — and
  // therefore the editor's internal config memo keyed on `controls` —
  // stays stable across renders.
  const controls = useMemo(
    (): Partial<Controls> => ({
      bold: { showInToolbar: true, title: t('rteBold') },
      italic: { showInToolbar: true, title: t('rteItalic') },
      underline: { showInToolbar: true, title: t('rteUnderline') },
      heading: {
        showInToolbar: true,
        title: [t('rteParagraph'), t('rteHeading1'), t('rteHeading2'), t('rteHeading3')],
      },
      bulletList: { showInToolbar: true, title: t('rteBulletList') },
      orderedList: { showInToolbar: true, title: t('rteOrderedList') },
      link: { showInToolbar: true, title: t('rteLink') },
      image: { showInToolbar: true, title: t('rteImage') },
      history: { showInToolbar: true, title: [t('rteUndo'), t('rteRedo')] },
      strike: false,
      code: false,
      blockquote: false,
      horizontalRule: false,
      clearFormat: false,
      table: false,
      fontColor: false,
      highlight: { showInToolbar: false, title: t('rteHighlight') },
      textAlign: false,
      indent: false,
    }),
    [t],
  );

  // Instance-scoped pending image selected in the custom image modal.
  // Lives in a ref (not state) because the modal submit handler needs
  // the latest value synchronously and no re-render should occur while
  // the FileReader resolves.
  const pendingImageRef = useRef<PendingImageHolder>({ dataUrl: null, file: null });

  const handleCreate = useCallback((editor: EditorInstance) => {
    editorRef.current = editor;
  }, []);

  const handleChange = useCallback(
    (editor: EditorInstance) => {
      const html = editor.getHTML();
      onChange(html);
    },
    [onChange],
  );

  // Keep the mounted editor in sync with external `value` changes.
  // TipTap consumes `defaultContent` only at editor creation, so a
  // description that resolves after mount (e.g. an existing draft
  // loading while the sheet is open) must be pushed imperatively.
  // Guarded so user typing — where `value` already mirrors the editor
  // HTML via onChange — is a no-op and the cursor is preserved.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.content.set(value);
    }
  }, [value]);

  const renderImageModalContent = useCallback(
    (): React.ReactNode => (
      <ImageUploadModalContent pendingImage={pendingImageRef.current} />
    ),
    [],
  );

  // Intercepts the modal form submit: reads the pending data URL
  // stored by this instance's modal content and inserts it via setImage.
  const handleImageSubmit = useCallback(
    (
      event: React.MouseEvent<HTMLFormElement>,
      setImage: (params: { src: string }) => void,
    ): void => {
      event.preventDefault();
      const holder = pendingImageRef.current;
      if (holder.dataUrl) {
        setImage({ src: holder.dataUrl });
        holder.dataUrl = null;
      }
    },
    [],
  );

  return (
    <div data-testid={dataTestId}>
      <RichTextEditor
        {...(ariaLabel != null && { 'aria-label': ariaLabel })}
        defaultContent={value}
        controls={controls}
        onChange={handleChange}
        onCreate={handleCreate}
        renderImageModalContent={renderImageModalContent}
        handleImageModalOnSubmit={handleImageSubmit}
        hasWordCounter
        recommendedWordCount={2000}
        recommendedWordCountTokenType={TokenType.Characters}
        {...(placeholder != null && { placeholder })}
        disabled={disabled}
        defaultHeight={EDITOR_DEFAULT_HEIGHT}
        maxHeight={EDITOR_MAX_HEIGHT}
      />
    </div>
  );
}
